import { OAuth2 } from '@backstage/core-app-api';
import {
  ApiBlueprint,
  BackstageIdentityApi,
  configApiRef,
  coreExtensionData,
  createApiRef,
  createExtension,
  createFrontendModule,
  discoveryApiRef,
  githubAuthApiRef,
  gitlabAuthApiRef,
  OAuthApi,
  oauthRequestApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  useApi,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import { ProviderSettingsItem } from '@backstage/plugin-user-settings';
import StarIcon from '@material-ui/icons/Star';
import { scmIntegrationsApiRef, ScmIntegrationsApi, scmAuthApiRef, ScmAuth } from '@backstage/integration-react';

export const keycloakAuthApiRef = createApiRef<
  OAuthApi & OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>().with({
  id: 'auth.keycloak',
});

export const keycloakAuthApi = ApiBlueprint.make({
  name: 'keycloak',
  params: defineParams =>
    defineParams({
      api: keycloakAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          provider: {
            id: 'oidc',
            title: 'Keycloak',
            icon: () => null,
          },
          defaultScopes: ['openid', 'profile', 'email', 'offline_access'],
        }),
    }),
});

export const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => {
      const configApi = useApi(configApiRef);            
      const isDevelopment = configApi.getOptionalString('auth.environment') === 'development';

      if (isDevelopment) {
        return <SignInPage {...props} auto providers={['guest']} />;
      }
      
      return (
        <SignInPage
          {...props}
          provider={{
              id: 'keycloak-auth-provider',
              title: 'Keycloak',
              message: 'Sign In using Keycloak',
              apiRef: keycloakAuthApiRef,
            }}
        />
      )},
  },
});

const authProviderSettings = createExtension({
  name: 'exx-provider-settings',
  attachTo: {
    id: 'sub-page:user-settings/auth-providers',
    input: 'providerSettings',
  },
  output: [coreExtensionData.reactElement],
  factory() {
    return [
      coreExtensionData.reactElement(
        <>
          <ProviderSettingsItem
            title="eXXcellent SSO"
            description="Provides sign-in via eXXcellent SSO (Keycloak)"
            apiRef={keycloakAuthApiRef}
            icon={StarIcon}
          />
          <ProviderSettingsItem
            title="GitHub"
            description="Provides authentication towards GitHub APIs"
            apiRef={githubAuthApiRef}
            icon={StarIcon}
          />
          <ProviderSettingsItem
            title="GitLab-ext"
            description="Provides authentication towards GitLab-EXT APIs"
            apiRef={gitlabAuthApiRef}
            icon={StarIcon}
          />
        </>,
      ),
    ];
  },
});

export const userSettingsAuthModule = createFrontendModule({
  pluginId: 'user-settings',
  extensions: [authProviderSettings],
});

// 1. SCM Integrations
export const scmIntegrationsApi = ApiBlueprint.make({
  name: 'scm-integrations',
  params: defineParams =>
    defineParams({
      api: scmIntegrationsApiRef,
      deps: { configApi: configApiRef },
      factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
    }),
});

// 2. SCM Auth (custom merge)
export const scmAuthApi = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams =>
    defineParams({
      api: scmAuthApiRef,
      deps: {
        gitlabAuthApi: gitlabAuthApiRef,
        githubAuthApi: githubAuthApiRef,
      },
      factory: ({ githubAuthApi, gitlabAuthApi }) =>
        ScmAuth.merge(
          ScmAuth.forGitlab(gitlabAuthApi, {
            host: 'gitlab-ext.exxcellent.de',
          }),
          ScmAuth.forGithub(githubAuthApi),
        ),
    }),
});
